import { atom, useAtom } from 'jotai'
import { useEffect, useState } from 'react'

import { shallowOverwrite } from '../utils/objectUtils'

// We have to leave sourceSharedState and sourceSharedStateAtom as Record<string, unknown> types because we are storing multiple types
const sourceSharedState: Record<string, unknown> = {}
const syncAtom = atom<number>(0)

interface UseSharedStateParams<T extends object> {
  key: string
  namespace?: string
  /**
   * @notes We don't allow undefined as the initial state, because it can mess with the stateSource mechanism.
   * If you need undefined, wrap that value as a property on a state object
   */
  initialState: T
}

type SharedStateDeleter = () => void
type SharedStateUpdate<T extends object> = (newState: T) => void
type SharedStateControls<T extends object> = [
  T,
  SharedStateUpdate<T>,
  T,
  SharedStateDeleter,
]

/**
 * Use a shared state object that is accessible across the application based on the key and namespace
 * @returns {SharedStateControls<T>} A tuple with the following structure:
 * - `0` The shared state object. _This is a lifecycle version that is updated on the next render._ This object's reference will change on each render, don't watch it directly on useEffect or useCallback.
 * - `1` Function to update the shared state. _It applies immediately to the shared state source object and in the next render for the shared state object._
 * - `2` The source state object. _This is a static version of the state object that always has the latest values regardless of react lifecycle/renders._
 * - `3` Function to delete the source state object and livecycle state object. _This should be used cautiously since using any shared state after this will be bug prone._
 */
export const useSharedState = <T extends object>({
  key,
  namespace = 'global',
  initialState,
}: UseSharedStateParams<T>): SharedStateControls<T> => {
  const aggregateKey = `${namespace}-${key}`
  // Set initial state if it doesn't exist
  const sourceState = (sourceSharedState[aggregateKey] as T | undefined) ?? {
    ...initialState,
  }
  const [syncCount, setSyncCount] = useState(0)
  if (!sourceSharedState[aggregateKey]) {
    sourceSharedState[aggregateKey] = sourceState
  }

  const [, syncAllInstances] = useAtom(syncAtom)

  // Whenever the sync count changes for this instance, update the shared state
  useEffect(() => {
    if (syncCount > 0) {
      syncAllInstances((n) => n + 1)
    }
  }, [syncCount])

  const deleteSharedState: SharedStateDeleter = () => {
    // Delete the source state
    delete sourceSharedState[aggregateKey]
  }

  const setSharedState: SharedStateUpdate<T> = (newState: T) => {
    // Modify the shared state source with a shallow update so the object reference isn't changed
    shallowOverwrite(sourceState, newState)

    // Update the sync count to trigger a lifecycle update.
    // If you attempt to modify an atom from outside the react tree (such as in a setTimeout or api callback),
    // it will render each hook instance one render at a time instead of all at once (basically breaking react's batch update process).
    // So instead we trigger this specific hook's state, and that state change triggers the atom update in the react tree.
    setSyncCount((prev) => prev + 1)
  }

  // Return a copy of sourceState, so it appears as a new object reference (limiting the ability to modify the source state mid-render)
  // Eventually it might make sense to do a deep copy here, but for now we're keeping it shallow
  return [{ ...sourceState }, setSharedState, sourceState, deleteSharedState]
}
